import { FormAssistive, FormGroup, FormLabel, InputAffix } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React, { FunctionComponent, memo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikError, FormikTextInput, useFormikField, VALIDATION_TYPES } from "@/components/Formik";
import { UserContext } from "@/components/User";
import { VAT_PLACEHOLDERS } from "@/consts";
import { MEDIA_SIZE_XS } from "@/core/media";
import { Flex } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { useHasUserCompanyWithVat } from "@/views/components/Company/hooks";
import { Messenger } from "@/views/components/Messenger";

import { ICompanyFormikProps } from "../types";

export const COMPANY_VAT_NUMBER_FORM_MESSENGER_CONTEXT = "company-vat-form";

export const validationCompanyVatNumberSchema = Yup.object().shape({
  vatNo: Yup.string().nullable(true).required(VALIDATION_TYPES.REQUIRED),
});

export interface ICompanyVatNumberFormValues {
  countryCode: string;
  vatNo: string;
}

interface ICompanyVatNumberFormProps extends IEditableSectionRenderProps {
  form: FormikProps<ICompanyVatNumberFormValues>;
  onCancel: () => void;
}

export const CompanyVatNumberForm: FunctionComponent<ICompanyVatNumberFormProps> = memo(props => {
  const isVerySmallScreen = useMedia(MEDIA_SIZE_XS);

  const User = useContext(UserContext);

  const { t, i18n } = useTranslation("session");

  const isManager = User.hasCompanyManagerPrivileges();

  const hasCompanyWithVat = useHasUserCompanyWithVat();

  const VAT_NO_OVERRIDE_MESSAGES = {
    [VALIDATION_TYPES.NOT_UNIQUE]: hasCompanyWithVat(props.form.values.vatNo, props.form.values.countryCode)
      ? isManager
        ? t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.NOT_UNIQUE_HAS_COMPANY_MANAGER")
        : t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.NOT_UNIQUE_HAS_COMPANY_CONSULTANT")
      : t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.NOT_UNIQUE"),
    ALREADY_EXISTS: t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.ALREADY_EXISTS"),
    INVALID_VAT: t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.INVALID"),
    UNKNOWN_VAT: t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.UNKNOWN"),
    INVALID_SIZE: t("COMPANY.CREATE.ERROR_MESSAGES.VAT_NO.INVALID_SIZE"),
  };

  const vatNumberHintTranslationKey = `session:COMPANY.CREATE.VAT_FORMAT.${props.form.values.countryCode}`;

  useEditableSubmit(props.form, props.editableProps);

  const vatField = useFormikField({ name: "vatNo" });
  return (
    <FormikForm>
      <Flex direction="column" innerMargin="md" margin={[isVerySmallScreen ? "lg" : false, false, false, false]}>
        <Messenger context={COMPANY_VAT_NUMBER_FORM_MESSENGER_CONTEXT} />

        <Flex innerMargin="md" grow={1} direction={isVerySmallScreen ? "column" : "row"}>
          <Flex direction="column" width={{ xxs: 12, xs: 5 }}>
            <FormGroup
              label={<FormLabel label={t("COMPANY.CREATE.VAT_NUMBER")} />}
              error={<FormikError name="vatNo" overrideMessages={VAT_NO_OVERRIDE_MESSAGES} />}
              assistive={
                props.form.values.countryCode && (
                  <FormAssistive
                    text={t(i18n?.exists(vatNumberHintTranslationKey) ? vatNumberHintTranslationKey : "")}
                  />
                )
              }>
              <InputAffix size="md" prefix={props.form.values.countryCode || ""} invalid={vatField.isErrorVisible}>
                <FormikTextInput
                  type="text"
                  name="vatNo"
                  value={props.form.values.vatNo}
                  required
                  size="md"
                  placeholder={
                    props.form.values.countryCode ? VAT_PLACEHOLDERS[props.form.values.countryCode] : undefined
                  }
                />
              </InputAffix>
            </FormGroup>
          </Flex>
        </Flex>
      </Flex>
    </FormikForm>
  );
});

interface ICompanyVatNumberFormikProps
  extends ICompanyVatNumberFormProps,
    ICompanyFormikProps<ICompanyVatNumberFormValues> {}

export const CompanyVatNumberFormik: FunctionComponent<ICompanyVatNumberFormikProps> = memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<ICompanyVatNumberFormValues>) => <CompanyVatNumberForm {...rest} form={form} />}
    />
  ),
);
